import React from "react";

// Convivían tres idiomas de espaciado incompatibles:
//   paddingBlock: clamp(60px, 8vw, 120px)   (carruseles)
//   py-24 px-6 md:px-12                      (Formación, Contacto, Misionero…)
//   py-16 px-4                               (Quiénes Somos, Equipo)
// Por eso el ritmo vertical cambiaba de una página a otra.
const TONES = {
  light: "section-bg-light",
  muted: "section-bg-muted",
  dark: "section-bg-dark",
};

const WIDTHS = {
  narrow: "max-w-3xl",
  reading: "max-w-4xl",
  default: "max-w-7xl",
  wide: "max-w-[1400px]",
  full: "",
};

const SPACES = {
  none: "0",
  tight: "clamp(40px, 5vw, 72px)",
  default: "clamp(60px, 8vw, 120px)",
  loose: "clamp(80px, 12vw, 160px)",
};

/**
 * Envoltorio de sección: tono de fondo, espaciado vertical y ancho máximo.
 *
 * `tone` es explícito a propósito. Antes el fondo se derivaba de la paridad del
 * índice de sección, lo que producía un rayado blanco/gris constante en toda la
 * portada. Nombrar el tono permite agrupar por capítulos: varias secciones
 * claras seguidas y luego una oscura a sangre como puntuación.
 */
export default function Section({
  children,
  tone = "light",
  width = "default",
  space = "default",
  className = "",
  innerClassName = "",
  as: Tag = "section",
  ...rest
}) {
  const widthClass = WIDTHS[width] ?? WIDTHS.default;
  return (
    <Tag
      className={`${TONES[tone] ?? TONES.light} px-6 md:px-12 ${className}`}
      style={{ paddingBlock: SPACES[space] ?? SPACES.default }}
      {...rest}
    >
      <div className={`${widthClass} mx-auto ${innerClassName}`}>{children}</div>
    </Tag>
  );
}

/**
 * Cabecera de sección: antetítulo, titular y descripción.
 *
 * El trío estaba reescrito a mano en CarouselSection, StatsSection,
 * SocialSection, GallerySection y media docena de páginas, cada una con su
 * propio clamp() y su propio margen inferior.
 */
export function SectionHeading({
  label,
  title,
  description,
  align = "left",
  className = "",
  children,
}) {
  const centered = align === "center";
  return (
    <div
      className={`mb-12 ${centered ? "mx-auto text-center max-w-3xl" : "max-w-2xl"} ${className}`}
    >
      {label && (
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary mb-3">
          {label}
        </p>
      )}
      {title && (
        <h2
          className="font-heading font-bold text-balance"
          style={{ fontSize: "var(--text-h2)", letterSpacing: "-0.03em", lineHeight: 1.08 }}
        >
          {title}
        </h2>
      )}
      {description && (
        <p className="text-muted-foreground mt-4 text-lg leading-relaxed">{description}</p>
      )}
      {children}
    </div>
  );
}
